import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Minus, Plus, Trash2, ShoppingBag, X, Users, Pencil } from "lucide-react";
import { money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useState } from "react";

const MIN_ORDER = 40;

export default function CartDrawer() {
  const { open, closeCart, items, updateQty, removeItem, editLine, subtotal } = useCart();
  const nav = useNavigate();
  const [editing, setEditing] = useState(null);

  const belowMin = subtotal < MIN_ORDER && items.length > 0;

  const goCheckout = () => {
    if (belowMin) return;
    closeCart();
    nav("/commander");
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && closeCart()}>
      <SheetContent side="right" data-testid="cart-drawer" className="w-full sm:max-w-md bg-card border-l border-border text-foreground p-0 flex flex-col">
        <SheetTitle className="sr-only">Votre panier</SheetTitle>
        <div className="flex items-center justify-between p-5 border-b border-border">
          <h2 className="font-heading text-lg font-bold flex items-center gap-2">
            <ShoppingBag className="h-5 w-5 text-coral" /> Votre panier
          </h2>
          <button onClick={closeCart} data-testid="cart-close" className="p-2 rounded-full hover:bg-foreground/10"><X className="h-5 w-5" /></button>
        </div>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 p-8 text-center">
            <div className="h-20 w-20 rounded-full bg-foreground/5 flex items-center justify-center">
              <ShoppingBag className="h-9 w-9 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground">Votre panier est vide.</p>
            <button onClick={() => { closeCart(); nav("/menu"); }} className="px-5 py-2.5 rounded-full bg-primary text-primary-foreground hover:opacity-90 font-heading text-sm font-medium transition-colors">
              Parcourir le menu
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {items.map((it) => (
                <div key={it.lineId} data-testid={`cart-item-${it.product_id}`} className="flex gap-3 p-3 rounded-xl bg-foreground/5 border border-border">
                  <img src={it.image} alt={it.nom} className="h-20 w-20 rounded-lg object-cover shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="font-heading font-semibold text-sm text-foreground leading-snug">{it.nom}</h4>
                      <button onClick={() => removeItem(it.lineId)} data-testid={`cart-remove-${it.product_id}`} className="text-muted-foreground hover:text-red-400 transition-colors shrink-0">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    {it.guests && (
                      <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1"><Users className="h-3 w-3 text-coral" /> {it.guests} invités</p>
                    )}
                    {it.options && it.options.length > 0 && (
                      <p className="text-xs text-muted-foreground mt-0.5">{it.options.map((o) => o.nom).join(", ")}</p>
                    )}
                    {it.note && <p className="text-xs text-muted-foreground mt-0.5 italic truncate">« {it.note} »</p>}
                    <div className="flex items-center justify-between mt-2">
                      <div className="inline-flex items-center rounded-full bg-ink-900 border border-border">
                        <button onClick={() => updateQty(it.lineId, it.quantity - 1)} data-testid={`cart-minus-${it.product_id}`} className="h-8 w-8 flex items-center justify-center hover:bg-foreground/10 rounded-full"><Minus className="h-3.5 w-3.5" /></button>
                        <span className="w-8 text-center text-sm font-bold" data-testid={`cart-qty-${it.product_id}`}>{it.quantity}</span>
                        <button onClick={() => updateQty(it.lineId, it.quantity + 1)} data-testid={`cart-plus-${it.product_id}`} className="h-8 w-8 flex items-center justify-center hover:bg-foreground/10 rounded-full"><Plus className="h-3.5 w-3.5" /></button>
                      </div>
                      <span className="font-heading font-bold text-coral">{money(it.prix * it.quantity)}</span>
                    </div>
                    {it.configurable && !it.kind && (
                      <button onClick={() => setEditing(editing === it.lineId ? null : it.lineId)} className="mt-2 text-xs text-muted-foreground hover:text-coral flex items-center gap-1">
                        <Pencil className="h-3 w-3" /> Modifier la configuration
                      </button>
                    )}
                    {editing === it.lineId && (
                      <div className="mt-2 p-2 rounded-lg bg-ink-900/60 space-y-2">
                        <label className="text-xs text-muted-foreground">Nombre d'invités</label>
                        <input type="number" min={1} defaultValue={it.guests || it.servings}
                          onChange={(e) => editLine(it.lineId, { guests: Math.max(1, parseInt(e.target.value) || 1) })}
                          className="w-full px-3 py-1.5 rounded bg-foreground/5 border border-border text-sm" />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="p-5 border-t border-border glass space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Sous-total</span>
                <span className="font-heading text-xl font-bold" data-testid="cart-subtotal">{money(subtotal)}</span>
              </div>
              {belowMin && (
                <p data-testid="cart-min-warning" className="text-xs text-amber-400 bg-amber-400/10 border border-amber-400/20 rounded-lg px-3 py-2">
                  Minimum de commande : {money(MIN_ORDER)}. Ajoutez {money(MIN_ORDER - subtotal)} pour continuer.
                </p>
              )}
              <button
                onClick={goCheckout}
                disabled={belowMin}
                data-testid="cart-checkout-button"
                className="w-full py-3.5 rounded-xl bg-primary hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed text-primary-foreground font-heading font-semibold transition-colors active:scale-[0.98]"
              >
                Passer à la commande
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
